const { ethers, utils, BigNumber } = require('ethers');
const axios = require('axios');
const { gql, request } = require('graphql-request');
const _ = require('lodash');
const { customErrors, _extractChainID, _getChainSpecificConstants, ZERO, ONE_ETHER } = require('./constants');
const { getContractInstance } = require('./contracts');
const { _add0x } = require('./utils');
const { stakehouseContracts } = require("@blockswaplab/stakehouse-protocol-abis/index");
const { _getDepositDataFromKeystore } = require('./BLSCredentialGenerator');

const MINIMUM_DEPOSIT_AMOUNT = ethers.utils.parseUnits("0.001", "ether");

const _depositETHForProtectedStaking = async (signer, amount) => {

    if(amount < MINIMUM_DEPOSIT_AMOUNT) {
        throw new Error(customErrors.INSUFFICIENT_DEPOSIT_AMOUNT);
    }

    const contract = (await getContractInstance(signer)).giantSavETHPool();

    return contract.depositETH(
        amount,
        { value: amount }
    );
};

const _depositETHForFeesAndMEV = async (signer, amount) => {

    if(amount < MINIMUM_DEPOSIT_AMOUNT) {
        throw new Error(customErrors.INSUFFICIENT_DEPOSIT_AMOUNT);
    }

    const contract = (await getContractInstance(signer)).giantFeesAndMEV();

    return contract.depositETH(
        amount,
        { value: amount }
    );
};

const _getListOfLSDNetworks = async (signer) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getAllLSDN {
			liquidStakingNetworks(first:1000) {
                liquidStakingManager
                ticker
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery
	);

    if (!response || !response.liquidStakingNetworks) {
		throw new Error('Invalid response getting the list of LSD');
	}

    return response.liquidStakingNetworks;
};

const _getLSDNForBLSPublicKey = async (signer, blsPublicKey) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getAllLSDN($id: ID!) {
            lsdvalidator(id: $id) {
                smartWallet {
                    liquidStakingNetwork {
                        liquidStakingManager
                    }
                }
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery, 
        {
            id: _add0x(blsPublicKey.toLowerCase())
        }
	);

    if (!response || !response.lsdvalidator) {
		throw new Error('Invalid response getting the LSDN associated with the BLS public key');
	}

    return response.lsdvalidator.smartWallet.liquidStakingNetwork.liquidStakingManager;
};

const _getSafeOwners = async (safeAddress) => {

    const client = axios.create({
        baseURL: 'https://safe-transaction-goerli.safe.global/',
        headers: {
          'Accept': 'application/json'
        }
    });

    let res;
    
    try {
        res = await client.get(`api/v1/safes/${_add0x(safeAddress)}/`);
    } catch(e) {
        console.log("error getting safe: ", e.response.status);
        return;
    }

    return res.data.owners;
};

const _depositETHByNodeRunner = async (signer, liquidStakingManagerAddress, blsPublicKey, blsSignature, eoaRepresentative, ethAmount) => {

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);
    
    try {
        const safeOwners = await _getSafeOwners(eoaRepresentative);

        if(safeOwners.length > 0) {
            eoaRepresentative = safeOwners[0];
        }
    }
    catch(e) {
        // do nothing
    }

    return contract.registerBLSPublicKeys(
        [_add0x(blsPublicKey)],
        [_add0x(blsSignature)],
        _add0x(eoaRepresentative),
        { value: ethAmount }
    );
};

const _availableToStake = async (signer) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getAvailableToStake {
            giantSavETHPools {
              availableToStake
            }
            giantFeesAndMevPools {
              availableToStake
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery
	);

    if (!response || !response.giantSavETHPools || !response.giantFeesAndMevPools) {
		throw new Error('Invalid response getting the ready to stake amount from the giant pool');
	}

    return {
        savETH: response.giantSavETHPools[0].availableToStake,
        FeesAndMev: response.giantFeesAndMevPools[0].availableToStake
    }
};

const _isBLSPublicKeyBanned = async (signer, liquidStakingManagerAddress, blsPublicKey) => {

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);
    
    const isBanned = await contract.isBLSPublicKeyBanned(blsPublicKey);
    if(isBanned) {
        throw new Error(customErrors.BANNED_BLS_PUBLIC_KEY);
    }
    
    return false;
};

const _getLPTokenForBLSPublicKey = async (signer, blsPublicKey, tokenType) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getLPTokens($bls: String!, $tokenType: String!) {
            lptokens(
                where:{
                    blsPublicKey: $bls
                    tokenType: $tokenType
                }
            ) {
              id
              minted
              giantPoolBalance
              issuer
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        { 
            bls: _add0x(blsPublicKey.toLowerCase()),
            tokenType: tokenType
        }
	);

    if (!response || !response.lptokens) {
		throw new Error('No LP token found');
	}

    return response.lptokens[0];
};

const _calculateFundsRequiredForStaking = async (signer, blsPublicKey) => {

    const protectedStakingLPpromise = _getLPTokenForBLSPublicKey(signer, blsPublicKey, "PROTECTED_STAKING_LP");
    const feesAndMevLPpromise = _getLPTokenForBLSPublicKey(signer, blsPublicKey, "FEES_AND_MEV_LP");
    const fundsPromise = _availableToStake(signer);

    let savETH = ZERO;
    let feesAndMEV = ZERO;

    await Promise.allSettled([protectedStakingLPpromise, feesAndMevLPpromise, fundsPromise]).then(
        async(result) => {
            const protectedStakingLP = result[0].value;
            const feesAndMevLP = result[1].value;
            const funds = result[2].value;

            const giantSavETHBalance = BigNumber.from(funds.savETH);
            const giantFeesAndMevBalance = BigNumber.from(funds.FeesAndMev);

            if(protectedStakingLP !== undefined) {
                const minted = BigNumber.from(protectedStakingLP.minted);

                if(minted.lt(ONE_ETHER.mul('24'))) {
                    savETH = ONE_ETHER.mul('24').sub(minted);

                    if(giantSavETHBalance.lt(savETH) && !_isETHAvailableInProtectedStakingOfSomeLSDN(signer, savETH)) {
                        throw new Error(customErrors.INSUFFICIENT_GIANT_SAVETH_POOL_BALANCE);
                    }
                }
            } else {
                savETH = ONE_ETHER.mul('24')
            }

            if(feesAndMevLP !== undefined) {
                const minted = BigNumber.from(feesAndMevLP.minted);

                if(minted.lt(ONE_ETHER.mul('4'))) {
                    feesAndMEV = ONE_ETHER.mul('4').sub(minted);

                    if(giantFeesAndMevBalance.lt(feesAndMEV) && !_isETHAvailableInFeesAndMevOfSomeLSDN(signer, feesAndMEV)) {
                        throw new Error(customErrors.INSUFFICIENT_GIANT_STAKING_POOL_BALANCE);
                    }
                }
            } else {
                feesAndMEV = ONE_ETHER.mul('4')
            }
        }
    );
    
    return { savETH, feesAndMEV };
};

const _fundNodeOperatorFromGiantSavETHPool = async (signer, liquidStakingManagerAddress, blsPublicKey, amount) => {

    if(BigNumber.from(amount).gt(ONE_ETHER.mul('24'))) {
        throw new Error(customErrors.INCORRECT_FUND_AMOUNT_FOR_GIANT_SAVETH_VAULT);
    }

    blsPublicKey = _add0x(blsPublicKey);

    const liquidStakingManager = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    const savETHVault = await liquidStakingManager.savETHVault();

    const giantSavETHPool = (await getContractInstance(signer)).giantSavETHPool();

    return giantSavETHPool.batchDepositETHForStaking(
        [savETHVault], // address of savETH vault which gets ETH from giant pool
        [amount], // amount of ETH being transferred to savETH vault
        [[blsPublicKey]], // list of BLS public keys for which ETH is being deposited
        [[amount]] // amount of ETH to be deposited for each BLS public key
    );
};

const _fundNodeOperatorFromGiantFeesAndMevPool = async (signer, liquidStakingManagerAddress, blsPublicKey, amount) => {

    if(BigNumber.from(amount).gt(ONE_ETHER.mul('4'))) {
        throw new Error(customErrors.INCORRECT_FUND_AMOUNT_FOR_GIANT_FEES_AND_MEV);
    }

    blsPublicKey = _add0x(blsPublicKey);

    const liquidStakingManager = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    const stakingFundsVault = await liquidStakingManager.stakingFundsVault();

    const giantStakingFundsPool = (await getContractInstance(signer)).giantFeesAndMEV();

    return giantStakingFundsPool.batchDepositETHForStaking(
        [stakingFundsVault], // address of staking funds fault which gets ETH from giant pool
        [amount], // amount of ETH to be sent to the staking funds vault
        [[blsPublicKey]], // list of BLS public keys for which ETH is being deposited
        [[amount]] // amount of ETH to be deposited for each BLS public key
    );
};

const _stake = async (signer, liquidStakingManagerAddress, authenticationPackage) => {

    const { deposit_data, cipher_text, encryptor_aes_key, deadline, v, r, s } = authenticationPackage;
    const signature = { deadline, v, r: _add0x(r), s: _add0x(s) };
    blsPublicKey = _add0x(deposit_data[0].pubkey);

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    _isBLSPublicKeyBanned(signer, liquidStakingManagerAddress, blsPublicKey);

    return contract.stake(
        [blsPublicKey],
        [_add0x(cipher_text)],
        [encryptor_aes_key],
        [signature],
        [_add0x(deposit_data[0].deposit_data_root)]
    )
};

const _getAllBLSPublicKeysForANodeRunnerInAnLSDNetwork = async (signer, liquidStakingManagerAddress, nodeRunner) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getAllValidators($nodeRunner: String!, $lsdn: String!) {
            nodeRunners(
                where:{
                    id: $nodeRunner
                    liquidStakingNetworks_: {
                        id: $lsdn
                    }
                }
            ){
                id
                validators {
                    id
                }
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            nodeRunner: _add0x(nodeRunner.toLowerCase()),
            lsdn: _add0x(liquidStakingManagerAddress.toLowerCase())
        }
	);

    if (!response || !response.nodeRunners) {
		throw new Error('Invalid response getting the validators of the Node Runner');
	}

    return response.nodeRunners[0].validators;
};

const _getValidatorLifecycleStatus = async (signer, blsPublicKey) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getValidatorLifecycleStatus($bls: String!) {
            lsdvalidator(id: $bls) {
              status
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            bls: _add0x(blsPublicKey.toLowerCase())
        }
	);

    if (!response || !response.lsdvalidator) {
		throw new Error('Invalid response getting the status of lsdvalidator');
	}

    return response.lsdvalidator.status;
};

const _previewUnclaimedETHAsCollateralizedSlotOwner = async (syndicateInstance, smartWalletAddress, blsPublicKey) => {

    return syndicateInstance.previewUnclaimedETHAsCollateralizedSlotOwner(
        _add0x(smartWalletAddress),
        _add0x(blsPublicKey)
    );
};

const _previewNodeOperatorRewards = async (signer, syndicateAddress, liquidStakingManagerAddress, nodeRunnerAddress, smartWalletAddress) => {

    const contract = (await getContractInstance(signer)).syndicate(syndicateAddress);

    const blsPublicKeys = await _getAllBLSPublicKeysForANodeRunnerInAnLSDNetwork(signer, liquidStakingManagerAddress, nodeRunnerAddress);

    let listOfPromises = [];
    for(let i=0; i<blsPublicKeys.length; ++i) {
        const status = await _getValidatorLifecycleStatus(signer, blsPublicKeys[i].id);
        if(status !== "MINTED_DERIVATIVES") continue;

        const res = _previewUnclaimedETHAsCollateralizedSlotOwner(contract, smartWalletAddress, blsPublicKeys[i].id);
        listOfPromises.push(res);
    }

    let accumulatedRewards = BigNumber.from('0');

    await Promise.allSettled(listOfPromises).then(
        async(result) => {
            for(let i=0; i<result.length; ++i) {
                accumulatedRewards = accumulatedRewards.add(result[i].value)
            }
        }
    ).catch(
		(error) => {
			throw error;
		}
	);

	return accumulatedRewards;
};

const _getGiantPoolDETHBalance = async (signer, giantPoolAddress) => {

    const contract = (await getContractInstance(signer)).dETHContract();

    return contract.balanceOf(giantPoolAddress);
};

const _getUserGiantProtectedStakingLPBalance = async (signer, userAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseAddresses } = _getChainSpecificConstants(chainID);

    const contract = (await getContractInstance(signer)).genericERC20Contract(stakehouseAddresses.GIANT_SAVETH_LP);

    return contract.balanceOf(userAddress);
};

const _getUserGiantFeesAndMevLPBalance = async (signer, userAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseAddresses } = _getChainSpecificConstants(chainID);

    const contract = (await getContractInstance(signer)).genericERC20Contract(stakehouseAddresses.GIANT_FEES_AND_MEV_LP);

    return contract.balanceOf(userAddress);
};

const _getMinimum = (value1, value2) => {

    if(!ethers.BigNumber.isBigNumber(value1)) {
        value1 = ethers.BigNumber.from(value1);
    }

    if(!ethers.BigNumber.isBigNumber(value2)) {
        value2 = ethers.BigNumber.from(value2);
    }

    if(value1.lt(value2)) return value1;

    return value2;
};

const _sortAndCreateInputArrayForFeesAndMevRewards = (response) => {

    // 1D array containing all the Fees and MEV pool address of which
    // the Giant Fees and MEV pool holds LP tokens
    let listOfFeesAndMevPools = [];
    // 2D array of BLS public keys for each of the Fees anf MEV pools
    let listOfBlsPublicKeys = [];
    // temporary 1D array of BLS public keys for a single Fees and MEV pool
    let blsPublicKeys = [];

    let previousFeesAndMev = response[0].issuer;

    // Push existing Fees and MEV pool address to list of pool
    listOfFeesAndMevPools.push(previousFeesAndMev);

    // Traverse through the response array
    for(let i=0; i<response.length; ++i) {
        // If a new Fees and MEV pool address is encountered
        // Push the new address to listOfFeesAndMevPools
        // Push the 1D BLS public key array to 2D listOfBlsPublicKeys
        // Update previousFeesAndMev array and wipe the temporary 1D BLS public key array
        if(previousFeesAndMev !== response[i].issuer) {
            listOfFeesAndMevPools.push(_add0x(response[i].issuer));
            listOfBlsPublicKeys.push(blsPublicKeys);
            blsPublicKeys = [];
            previousFeesAndMev = response[i].issuer;
        }
        blsPublicKeys.push(response[i].blsPublicKey);
    }

    // Push the last 1D BLS public key array to 2D listOfBlsPublicKeys
    listOfBlsPublicKeys.push(blsPublicKeys);

    return { listOfFeesAndMevPools, listOfBlsPublicKeys };
};

const _getLpTokensDataForClaimRewards = async (signer, tokenType) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getLPTokens($tokenType: String!) {
            lptokens (where:{
                tokenType:$tokenType,
                giantPoolBalance_gt:0,
                lifecycleStatus: "MINTED_DERIVATIVES"
            } orderBy:issuer){
                id
                issuer
                blsPublicKey
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            tokenType: tokenType
        }
	);

    if (!response || !response.lptokens) {
		throw new Error('Invalid response getting the list of LP tokens that the Giant pool holds');
	}

    return response.lptokens;
};

const _createInputArrayForFeesAndMevRewards = async (response) => {

    let listOfFeesAndMevPools = [];
    let listOfBlsPublicKeys = [];
    let listOfLPTokens = [];
    let lpTokens = [];
    let blsPublicKeys = [];
    let listOfAmounts = [];
    let amounts = [];
    let previousFeesAndMevAddress = "";
    let currentIssuer = "";

    for(let i=0; i<response.length; ++i) {
        currentIssuer = response[i].vaultLPToken.issuer;

        const res = response[i];
        const liquidity = res.liquidityProviders[0].amount;
        const withdrawn = res.liquidityProviders[0].withdrawn;
        const liquidityBN = ethers.BigNumber.from(liquidity);
        const withdrawnBN = ethers.BigNumber.from(withdrawn);
        const claimableAmount = liquidityBN.sub(withdrawnBN);

        // Skip the batch if user has already withdrawn all the staked amount
        if(claimableAmount.eq(ZERO)) {
            continue;
        }

        if(previousFeesAndMevAddress === "") {
            // push current LP token data into the list
            lpTokens.push(res.vaultLPToken.id);
            // push current BLS public key into the list
            blsPublicKeys.push(res.blsPublicKey);
            // update previous issuer with current one
            previousFeesAndMevAddress = currentIssuer;
            amounts.push(claimableAmount.toString());

            continue;
        }

        // If a new Fees and MEV pool is found
        // Add it to the listOfFeesAndMevPools
        // Push 1D temporary lpTokens array to listOfLPTokens
        // Push 1D temporary amounts array to listOfAmounts
        // Wipe the temporary array
        // Update the previousFeesAndMevAddress
        if(previousFeesAndMevAddress !== currentIssuer) {
            listOfFeesAndMevPools.push(_add0x(previousFeesAndMevAddress));
            listOfBlsPublicKeys.push(blsPublicKeys);
            listOfLPTokens.push(lpTokens);
            listOfAmounts.push(amounts);
            
            blsPublicKeys = [];
            lpTokens = [];
            amounts = [];

            previousFeesAndMevAddress = currentIssuer;
        }

        blsPublicKeys.push(res.blsPublicKey);
        lpTokens.push(res.vaultLPToken.id);
        amounts.push(claimableAmount.toString());
    }

    if(amounts.length > 0) {
        // push last temporary 1D array to associated array
        listOfFeesAndMevPools.push(currentIssuer);
        listOfBlsPublicKeys.push(blsPublicKeys);
        listOfLPTokens.push(lpTokens);
        listOfAmounts.push(amounts);
    }

    return { listOfFeesAndMevPools, listOfBlsPublicKeys, listOfLPTokens, listOfAmounts };
};

const _claimFeesAndMevRewards = async (signer, userAddress) => {

    let response = await _getFeesAndMevBatchesForDepositor(signer, userAddress, "MINTED_DERIVATIVES");

    response = _sortResponse(response);

    const result = await _createInputArrayForFeesAndMevRewards(response);

    const contract = (await getContractInstance(signer)).giantFeesAndMEV();

    return contract.claimRewards(
        userAddress,
        result.listOfFeesAndMevPools,
        result.listOfBlsPublicKeys
    );
};

const _getTokenBalance = async (signer, lpTokenAddress, userAddress) => {

    const contract = (await getContractInstance(signer)).genericERC20Contract(lpTokenAddress);

    return contract.balanceOf(userAddress);
};

const _sortResponse = (res) => {

    // 2D array which saves res values with same issuer in same row
    let responseArray = [];

    // Mapping for issuer <> row number
    let map = new Map(); 

    for(let i=0; i<res.length; ++i) {
        const issuer = res[i].vaultLPToken.issuer;
        // check if issuer exists in the mapping
        if(map.has(issuer)) {
            const row = map.get(issuer);
            responseArray[row].push(res[i]);
        }
        else {
            map.set(issuer, responseArray.length);
            responseArray.push([res[i]]);
        }
    }

    return responseArray.flat();
};

const _createInputArrayForProtectedStakingRewards = async (response, amount) => {

    let maxAmount = ethers.BigNumber.from(amount);

    let listOfProtectedStakingPools = [];
    let listOfLPTokens = [];
    let lpTokens = [];
    let listOfAmounts = [];
    let amounts = [];
    let previousProtectedStakingAddress = "";
    let currentIssuer = "";

    for(let i=0; i<response.length && maxAmount.gt(ZERO); ++i) {
        currentIssuer = response[i].vaultLPToken.issuer;

        const res = response[i];
        const liquidity = res.liquidityProviders[0].amount;
        const withdrawn = res.liquidityProviders[0].withdrawn;
        const liquidityBN = ethers.BigNumber.from(liquidity);
        const withdrawnBN = ethers.BigNumber.from(withdrawn);
        const claimableAmount = liquidityBN.sub(withdrawnBN);

        // Skip the batch if user has already withdrawn all the staked amount
        if(claimableAmount.eq(ZERO)) {
            continue;
        }

        if(previousProtectedStakingAddress === "") {
            // push current LP token data into the list
            lpTokens.push(res.vaultLPToken.id);
            // update previous issuer with current one
            previousProtectedStakingAddress = currentIssuer;

            if(maxAmount.gte(claimableAmount)) {
                amounts.push(claimableAmount.toString());
                maxAmount = maxAmount.sub(claimableAmount);
            }
            else {
                amounts.push(maxAmount.toString());
                maxAmount = ZERO;
            }

            continue;
        }

        // If a new Protected Staking pool is found
        // Add it to the listOfProtectedStakingPools
        // Push 1D temporary lpTokens array to listOfLPTokens
        // Push 1D temporary amounts array to listOfAmounts
        // Wipe the temporary array
        // Update the previousProtectedStakingAddress
        if(previousProtectedStakingAddress !== currentIssuer) {
            listOfProtectedStakingPools.push(_add0x(previousProtectedStakingAddress));
            listOfLPTokens.push(lpTokens);
            listOfAmounts.push(amounts);
            
            lpTokens = [];
            amounts = [];

            previousProtectedStakingAddress = currentIssuer;
        }

        lpTokens.push(res.vaultLPToken.id);

        if(maxAmount.gte(claimableAmount)) {
            amounts.push(claimableAmount.toString());
            maxAmount = maxAmount.sub(claimableAmount);
        }
        else {
            amounts.push(maxAmount.toString());
            maxAmount = ZERO;
        }
    }

    if(amounts.length > 0) {
        // push last temporary 1D array to associated array
        listOfProtectedStakingPools.push(currentIssuer);
        listOfLPTokens.push(lpTokens);
        listOfAmounts.push(amounts);
    }

    return { listOfProtectedStakingPools, listOfLPTokens, listOfAmounts };
};

const _getProtectedBatchesForDepositor = async (signer, userAddress, status) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getProtectedBatches($lpAddress: String! $status: String!) {
            protectedBatches(where:{
              liquidityProviders_:{
                lpAddress: $lpAddress
              }
              vaultLPToken_:{
                lifecycleStatus: $status
              }
            }) {
              id
              blsPublicKey
              vaultLPToken {
                id
                issuer
              }
              liquidityProviders(where:{
                lpAddress: $lpAddress
              }) {
                amount
                withdrawn
              }
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            lpAddress: _add0x(userAddress.toLowerCase()),
            status: status
        }
	);

    if (!response || !response.protectedBatches) {
		throw new Error('Invalid response getting the protected batches');
	}

    return response.protectedBatches;
};

const _previewClaimableProtectedStakingLP = async (signer, userAddress) => {

    const res = await _getProtectedBatchesForDepositor(signer, userAddress, "MINTED_DERIVATIVES");

    let rewards = ZERO;

    for(let i=0; i<res.length; ++i) {
        const batchReward = res[i].liquidityProviders[0].amount;
        const withdrawn = res[i].liquidityProviders[0].withdrawn;
        rewards = rewards.add(ethers.BigNumber.from(batchReward)).sub(ethers.BigNumber.from(withdrawn));
    }

    return rewards;
};

const _claimProtectedStakingRewards = async (signer, userAddress, amount) => {

    const response = await _getProtectedBatchesForDepositor(signer, userAddress, "MINTED_DERIVATIVES");

    const res = await _createInputArrayForProtectedStakingRewards(_sortResponse(response), amount);

    const contract = (await getContractInstance(signer)).giantSavETHPool();

    return contract.withdrawDETH(
        res.listOfProtectedStakingPools,
        res.listOfLPTokens,
        res.listOfAmounts
    );
};

const _bringUnusedETHBackIntoGiantFeesAndMevPool = async (signer, feesAndMevPoolAddresses, lpTokens, amounts) => {

    const contract = (await getContractInstance(signer)).giantFeesAndMEV();

    return contract.bringUnusedETHBackIntoGiantPool(
        feesAndMevPoolAddresses,
        lpTokens,
        amounts
    );
};

const _bringUnusedETHBackIntoGiantProtectedStakingPool = async (signer, savETHVaultAddresses, lpTokens, amounts) => {

    const contract = (await getContractInstance(signer)).giantSavETHPool();

    return contract.bringUnusedETHBackIntoGiantPool(
        savETHVaultAddresses,
        lpTokens,
        amounts
    );
};

const _getValidatorsToBeRotated = async (signer) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getAllValidators($time: BigInt!) {
            lsdvalidators(where: {
              totalETHFundedFromGiantPool_gt: 0
              giantPoolLastFundedTimestamp_lt: $time
              status_in:["WAITING_FOR_ETH", "READY_TO_STAKE"]
            } orderBy: liquidStakingManager
            ) {
              id
              liquidStakingManager
            }
        }
	`;

    const currentBlock = await signer.provider.getBlockNumber();
    const blockTimestamp = (await signer.provider.getBlock(currentBlock)).timestamp;
    const pastTimestamp = blockTimestamp - (30 * 60);

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            time: pastTimestamp
        }
	);

    if (!response || !response.lsdvalidators) {
		throw new Error('Invalid response getting the validators');
	}

    return response.lsdvalidators;
};

const _createInputParameters = async (signer, validators, tokenType) => {

    let listOfPools = [];
    let listofLPTokens = [];
    let listOfAmounts = [];
    let tokens = [];
    let amounts = [];
    let lsm = validators[0].liquidStakingManager;

    for(let i=0; i<validators.length; ++i) {
        const validatorInfo = await _getLPTokenForBLSPublicKey(signer, validators[i].id, tokenType);

        if(i == 0) {
            listOfPools.push(_add0x(validatorInfo.issuer));
        }

        if(lsm != validators[i].liquidStakingManager) {
            listOfPools.push(_add0x(validatorInfo.issuer));
            listofLPTokens.push(tokens);
            listOfAmounts.push(amounts);
            tokens = [];
            amounts = [];
            lsm = validators[i].liquidStakingManager;
        }
        
        tokens.push(_add0x(validatorInfo.id));
        amounts.push(ethers.BigNumber.from(validatorInfo.giantPoolBalance));
    }

    listofLPTokens.push(tokens);
    listOfAmounts.push(amounts);

    return { listOfPools, listofLPTokens, listOfAmounts };
};

const _rotateFundsBackToGiantProtectedStakingPool = async (signer) => {

    const validators = await _getValidatorsToBeRotated(signer);

    const protectedStakingInputs = await _createInputParameters(signer, validators, "PROTECTED_STAKING_LP");

    return _bringUnusedETHBackIntoGiantProtectedStakingPool(
        signer,
        protectedStakingInputs.listOfPools,
        protectedStakingInputs.listofLPTokens,
        protectedStakingInputs.listOfAmounts
    );
};

const _rotateFundsBackToGiantFeesAndMevPool = async (signer) => {

    const validators = await _getValidatorsToBeRotated(signer);

    const feesAndMevInputs = await _createInputParameters(signer, validators, "FEES_AND_MEV_LP");

    return await _bringUnusedETHBackIntoGiantFeesAndMevPool(
        signer, 
        feesAndMevInputs.listOfPools,
        feesAndMevInputs.listofLPTokens,
        feesAndMevInputs.listOfAmounts
    );
};

const _isETHAvailableInProtectedStakingOfSomeLSDN = async (signer, requiredProtectedStakingAmount) => {

    const validators = await _getValidatorsToBeRotated(signer);
    
    const resOfProtectedStaking = await _createInputParameters(signer, validators, "PROTECTED_STAKING_LP");
    const protectedStakingAmounts = resOfProtectedStaking.listOfAmounts;

    let availableProtectedStakingAmount = ZERO;

    for(let i=0; i<protectedStakingAmounts.length; ++i) {
        
        for(let j=0; j<protectedStakingAmounts[i].length; ++j) {
            
            availableProtectedStakingAmount = availableProtectedStakingAmount.add(protectedStakingAmounts[i][j]);
        }
    }
    return availableProtectedStakingAmount.gte(requiredProtectedStakingAmount);
};

const _isETHAvailableInFeesAndMevOfSomeLSDN = async (signer, requiredFeesAndMevAmount) => {

    const validators = await _getValidatorsToBeRotated(signer);
    
    const resOfFeesAndMev = await _createInputParameters(signer, validators, "FEES_AND_MEV_LP");
    const feesAndMevAmounts = resOfFeesAndMev.listOfAmounts;

    let availableFeesAndMevAmount = ZERO;

    for(let i=0; i<feesAndMevAmounts.length; ++i) {
        
        for(let j=0; j<feesAndMevAmounts[i].length; ++j) {

            availableFeesAndMevAmount = availableFeesAndMevAmount.add(feesAndMevAmounts[i][j]);
        }
    }
    
    return availableFeesAndMevAmount.gte(requiredFeesAndMevAmount);
};

const _getFeesAndMevBatchesForDepositor = async (signer, userAddress, status) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
		query getFeesAndMevBatches($lpAddress: String! $status: String!) {
            feesAndMevBatches(where:{
              liquidityProviders_:{
                lpAddress: $lpAddress
              }
              vaultLPToken_:{
                lifecycleStatus: $status
              }
            }) {
              id
              blsPublicKey
              vaultLPToken {
                id
                issuer
              }
              liquidityProviders(where:{
                lpAddress: $lpAddress
              }) {
                amount
                withdrawn
              }
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            lpAddress: _add0x(userAddress.toLowerCase()),
            status: status
        }
	);

    if (!response || !response.feesAndMevBatches) {
		throw new Error('Invalid response getting the protected batches');
	}

    return response.feesAndMevBatches;
};

const _sortAndCreateInputArrayForFeesAndMevRewardsPreview = (response) => {

    // 1D array containing all the Fees and MEV pool address of which
    // the Giant Fees and MEV pool holds LP tokens
    let listOfFeesAndMevPools = [];
    // 2D array of LP Tokens for each of the Fees anf MEV pools
    let listOfLPTokens = [];
    // temporary 1D array of LP Tokens for a single Fees and MEV pool
    let lpTokens = [];

    let previousFeesAndMev = response[0].vaultLPToken.issuer;

    // Push existing Fees and MEV pool address to list of pool
    listOfFeesAndMevPools.push(previousFeesAndMev);

    // Traverse through the response array
    for(let i=0; i<response.length; ++i) {
        // If a new Fees and MEV pool address is encountered
        // Push the new address to listOfFeesAndMevPools
        // Push the 1D LP Token array to 2D listOfLPTokens
        // Update previousFeesAndMev array and wipe the temporary 1D LP Tokens array
        if(previousFeesAndMev !== response[i].vaultLPToken.issuer) {
            listOfFeesAndMevPools.push(_add0x(response[i].vaultLPToken.issuer));
            listOfLPTokens.push(lpTokens);
            lpTokens = [];
            previousFeesAndMev = response[i].vaultLPToken.issuer;
        }
        lpTokens.push(response[i].vaultLPToken.id);
    }

    // Push the last 1D LP Tokens to 2D listOfLPTokens
    listOfLPTokens.push(lpTokens);

    return { listOfFeesAndMevPools, listOfLPTokens };
};

const _previewFeesAndMevRewards = async (signer, userAddress) => {

    let response = await _getFeesAndMevBatchesForDepositor(signer, userAddress, "MINTED_DERIVATIVES");

    response = _sortResponse(response);

    const result = _sortAndCreateInputArrayForFeesAndMevRewardsPreview(response);

    const contract = (await getContractInstance(signer)).giantFeesAndMEV();

    return contract.previewAccumulatedETH(
        userAddress,
        result.listOfFeesAndMevPools,
        result.listOfLPTokens
    );
};

const _getLastInteractedTimestamp = async (signer, userAddress, giantTokenAddress) => {

    const contract = (await getContractInstance(signer)).giantLPToken(giantTokenAddress);

    return contract.lastInteractedTimestamp(
        _add0x(userAddress)
    );
};

const _isEligibleToInteractWithGiantLPToken = async (signer, lastInteractedTimestamp) => {

    const currentBlock = await signer.provider.getBlockNumber();
    const blockTimestamp = (await signer.provider.getBlock(currentBlock)).timestamp;
    const blockTimestampBN = ethers.BigNumber.from(blockTimestamp);

    const oneDay = 24 * 60 * 60;
    const eligibleTimestamp = lastInteractedTimestamp.add(ethers.BigNumber.from(oneDay));

    return blockTimestampBN.gte(eligibleTimestamp);
};

const _validatorQuery = async (signer, accountAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
    query Validators($account: String!) {
        stakehouseAccounts(
          where: { lifecycleStatus_gte: 2, depositor: $account }
          orderBy: registerValidatorBlockNumber
          orderDirection: asc
        ) {
          id
          depositTxHash
          lifecycleStatus
          totalDETHMinted
          totalCollateralizedSLOTInVaultFormatted
          totalSLOT
          sETHMinted
          mintFromBlockNumber
          stakeHouseMetadata {
            id
            sETH
            sETHTicker
            sETHExchangeRate
            sETHPayoffRateFormatted
          }
          knotMetadata {
            isPartOfIndex
            savETHIndexId
          }
        }
      }
	`;

    const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
        {
            account: _add0x(accountAddress.toLowerCase())
        }
	);

    if (!response || !response.stakehouseAccounts) {
		throw new Error('Invalid response getting the validators');
	}

    return response.stakehouseAccounts;
};

const _smartWalletQuery = async (signer, userAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
    query SmartWallet($account: String!) {
        nodeRunner(id: $account) {
          smartWallets {
            id
          }
        }
      }
	`;

    const response1 = await request(
		stakehouseUrls.V1_LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            account: _add0x(userAddress.toLowerCase())
        }
	);

    const response2 = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            account: _add0x(userAddress.toLowerCase())
        }
	);

    let res = [];

    if(response1) {
        if(response1.nodeRunner) {
            res = res.concat(response1.nodeRunner.smartWallets);
        }
    }

    if(response2) {
        if(response2.nodeRunner) {
            res = res.concat(response2.nodeRunner.smartWallets);
        }
    }

    return res;
};

const _getCumulativeListOfValidators = async (signer, userAddress) => {

    let listOfStakehouseAccounts = [];
    let data;

    try {
        // get all the smart wallets associated with the userAddress (node operator in LSD)
        data = await _smartWalletQuery(signer, userAddress);
    } catch(e) {
        // do nothing
    }

    // check if user has a smart wallet
    if(data) {
        // loop through the data and fetch stakehouse related data
        // add the stakehouse related data to the list
        for(let i=0; i<data.length; ++i) {
            const depositor = data[i].id;
            const validatorData = await _validatorQuery(signer, depositor);
            if(validatorData.length > 0) {
                for(let j=0; j<validatorData.length; ++j) {
                    if( parseInt(validatorData[j].lifecycleStatus, 10) >= parseInt("3", 10)) {
                        listOfStakehouseAccounts = listOfStakehouseAccounts.concat(validatorData[j]);
                    }
                }
            }
        }
    }

    // if the user is a solo-staker on Stakehouse, add it to the list
    const stakehouseUser = await _validatorQuery(signer, userAddress);
    listOfStakehouseAccounts = listOfStakehouseAccounts.concat(stakehouseUser);
    
    // contains list of all the stakehouse accounts associated with user's smart wallets
    return listOfStakehouseAccounts;
};

const _activityQuery = async (signer, accountAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
    query Activity($account: String!) {
        events(
          first:1000
          where: {
            key_in: [
              "INITIALS_REGISTERED"
              "DEPOSIT_REGISTERED"
              "NEW_HOUSE_MEMBER"
              "NEW_STAKEHOUSE_REGISTRY_DEPLOYED"
              "DETH_WITHDRAWN_INTO_OPEN_MARKET"
              "RAGE_QUIT"
              "KNOT_INSERTED_INTO_INDEX"
              "SIGNING_KEY_RE_ENCRYPTION"
              "SLOT_SLASHED"
              "DETH_REWARDS_MINTED"
              "SLOT_TOPPED_UP"
            ]
            from: $account
          }
          orderBy: blockNumber
          orderDirection: desc
        ) {
          id
          key
          value
          blsPubKeyForKnot
          blockNumber
        }
      }
	`;

    const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
        {
            account: _add0x(accountAddress.toLowerCase())
        }
	);

    if (!response || !response.events) {
		throw new Error('Invalid response getting the activity');
	}

    return response.events;
};

const _lsdActivityQuery = async (signer, accountAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery1 = gql`
        query Activity($account: String!) {
            events(
                first: 1000
                where: {
                    key_in: [
                        "ETH_DEPOSITED_BY_STAKER"
                        "LP_BURNED_FOR_ETH"
                        "GIANT_LP_SWAPPED"
                        "GIANT_LP_BURNED_FOR_DETH"
                        "SMART_WALLET_CREATED"
                        "NEW_VALIDATOR_REGISTERED"
                        "LP_TOKEN_ISSUED"
                        "LP_TOKEN_MINTED"
                        "KNOT_STAKED"
                        "STAKEHOUSE_CREATED"
                        "STAKEHOUSE_JOINED"
                        "DETH_CLAIMED"
                        "NODE_RUNNER_REWARDS_CLAIMED"
                        "FEES_AND_MEV_CLAIMED"
                    ]
                    from: $account
                }
                orderBy: blockNumber
                orderDirection: desc
            ) {
                id
                key
                value
                blsPubKeyForKnot
                blockNumber
            }
        }
	`;

    const lookupQuery2 = gql`
        query Activity($account: String!) {
            events(
                first: 1000
                where: {
                    key_in: [
                        "ETH_DEPOSITED_BY_STAKER"
                        "LP_BURNED_FOR_ETH"
                        "GIANT_LP_SWAPPED"
                        "GIANT_LP_BURNED_FOR_DETH"
                        "SMART_WALLET_CREATED"
                        "NEW_VALIDATOR_REGISTERED"
                        "LP_TOKEN_ISSUED"
                        "LP_TOKEN_MINTED"
                        "KNOT_STAKED"
                        "STAKEHOUSE_CREATED"
                        "STAKEHOUSE_JOINED"
                        "DETH_CLAIMED"
                        "NODE_RUNNER_REWARDS_CLAIMED"
                        "FEES_AND_MEV_CLAIMED"
                    ]
                    from: $account
                }
                orderBy: blockNumber
                orderDirection: desc
            ) {
                tx
                key
                value
                blsPubKeyForKnot
                blockNumber
            }
        }
	`;

    const response1 = await request(
		stakehouseUrls.V1_LSD_SUBGRAPH_ENDPOINT,
		lookupQuery1,
        {
            account: _add0x(accountAddress.toLowerCase())
        }
	);

    const response2 = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery2,
        {
            account: _add0x(accountAddress.toLowerCase())
        }
	);

    let res = [];

    if(response1) {
        if(response1.events) {
            res = res.concat(response1.events);
        }
    }

    if(response2) {
        if(response2.events) {
            res = res.concat(response2.events);
        }
    }

    return res;
};

const _getCumulativeActivity = async (signer, userAddress) => {

    let listOfActivities = [];

    const validatorData = await _lsdActivityQuery(signer, userAddress);
    listOfActivities = listOfActivities.concat(validatorData);

    // if the user is a solo-staker on Stakehouse, add it to the list
    const stakehouseUserActivity = await _activityQuery(signer, userAddress);
    listOfActivities = listOfActivities.concat(stakehouseUserActivity);

    listOfActivities.sort((a, b) => {
        return b.blockNumber - a.blockNumber;
    });

    // contains list of all the stakehouse accounts associated with user's smart wallets
    return listOfActivities;
};

const _houseCreatedQuery = async (signer, accountAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query HousesCreated($account: String!) {
            stakehouseAccounts(
                where: { lifecycleStatus_gte: 3, depositor: $account, depositorCreatedHouse: true }
            ) {
                id
            }
        }
	`;

    const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
        {
            account: _add0x(accountAddress.toLowerCase())
        }
	);

    if (!response || !response.stakehouseAccounts) {
		throw new Error('Invalid response getting the activity');
	}

    return response.stakehouseAccounts;
};

const _getCumulativeHouseCreated = async (signer, userAddress) => {

    let data;

    try {
        // get all the smart wallets associated with the userAddress (node operator in LSD)
        data = await _smartWalletQuery(signer, userAddress);
    } catch(e) {
        // do nothing
    }

    let listOfValidators = [];

    if(data) {
        // loop through the data and fetch stakehouse related data
        // add the stakehouse related data to the list
        for(let i=0; i<data.length; ++i) {
            const depositor = data[i].id;
            const validatorData = await _houseCreatedQuery(signer, depositor);
            listOfValidators = listOfValidators.concat(validatorData);
        }
    }

    // if the user is a solo-staker on Stakehouse, add it to the list
    const stakehouseUserActivity = await _houseCreatedQuery(signer, userAddress);
    listOfValidators = listOfValidators.concat(stakehouseUserActivity);

    // contains list of all the stakehouse accounts associated with user's smart wallets
    return listOfValidators;
};

const _houseJoinedQuery = async (signer, accountAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query ValidatorsJoined($account: String!) {
            stakehouseAccounts(
                where: { lifecycleStatus_gte: 3, depositor: $account, depositorCreatedHouse: false }
            ) {
                id
            }
        }
	`;

    const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
        {
            account: _add0x(accountAddress.toLowerCase())
        }
	);

    if (!response || !response.stakehouseAccounts) {
		throw new Error('Invalid response getting the activity');
	}

    return response.stakehouseAccounts;
};

const _getCumulativeHouseJoined = async (signer, userAddress) => {

    let data;
    let listOfValidators = [];

    try {
        // get all the smart wallets associated with the userAddress (node operator in LSD)
        data = await _smartWalletQuery(signer, userAddress);
    } catch(e) {
        // do nothing
    }

    if(data) {
        // loop through the data and fetch stakehouse related data
        // add the stakehouse related data to the list
        for(let i=0; i<data.length; ++i) {
            const depositor = data[i].id;
            const validatorData = await _houseJoinedQuery(signer, depositor);
            listOfValidators = listOfValidators.concat(validatorData);
        }
    }

    // if the user is a solo-staker on Stakehouse, add it to the list
    const stakehouseUserActivity = await _houseJoinedQuery(signer, userAddress);
    listOfValidators = listOfValidators.concat(stakehouseUserActivity);

    // contains list of all the stakehouse accounts associated with user's smart wallets
    return listOfValidators;
};

const _validatorsCreatedQuery = async (signer, accountAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query ValidatorsCreated($account: String!) {
            stakehouseAccounts(where: { lifecycleStatus_gte: 2, depositor: $account }) {
                id
            }
        }
	`;

    const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
        {
            account: _add0x(accountAddress.toLowerCase())
        }
	);

    if (!response || !response.stakehouseAccounts) {
		throw new Error('Invalid response getting the validators created');
	}

    return response.stakehouseAccounts;
};

const _getCumulativeValidatorsCreated = async (signer, userAddress) => {

    let data;

    try {
        // get all the smart wallets associated with the userAddress (node operator in LSD)
        data = await _smartWalletQuery(signer, userAddress);
    } catch(e) {
        // do nothing
    }

    let listOfValidators = [];

    if(data) {
        // loop through the data and fetch stakehouse related data
        // add the stakehouse related data to the list
        for(let i=0; i<data.length; ++i) {
            const depositor = data[i].id;
            const validatorData = await _validatorsCreatedQuery(signer, depositor);
            listOfValidators = listOfValidators.concat(validatorData);
        }
    }

    // if the user is a solo-staker on Stakehouse, add it to the list
    const stakehouseUserActivity = await _validatorsCreatedQuery(signer, userAddress);
    listOfValidators = listOfValidators.concat(stakehouseUserActivity);

    // contains list of all the stakehouse accounts associated with user's smart wallets
    return listOfValidators;
};

const _getIndexOwner = async (signer, userAddress) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
    query SmartWallet($account: String!) {
        smartWallets(where:{
          nodeRunner_:{
            id: $account
          }
        }) {
          liquidStakingNetwork {
            savETHPool
          }
        }
      }
	`;

    const response1 = await request(
		stakehouseUrls.V1_LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            account: _add0x(userAddress.toLowerCase())
        }
	);

    const response2 = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            account: _add0x(userAddress.toLowerCase())
        }
	);

    let res = [];

    if(response1) {
        if(response1.smartWallets) {
            res = res.concat(response1.smartWallets);
        }
    }

    if(response2) {
        if(response2.smartWallets) {
            res = res.concat(response2.smartWallets);
        }
    }

    return res;
};

const _validatorIndexQuery = async (signer, indexOwner) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query Knot($account: String!) {
            savETHIndexes(where: { indexOwner: $account }, orderBy: indexId, orderDirection: asc) {
                id
            }
        }
	`;

    const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
        {
            account: _add0x(indexOwner.toLowerCase())
        }
	);

    if (!response || !response.savETHIndexes) {
		throw new Error('Invalid response getting the validators indexes');
	}

    return response.savETHIndexes;
};

const _getCumulativeValidatorIndexes = async (signer, userAddress) => {

    let data;

    try {
        // get all the smart wallets associated with the userAddress (node operator in LSD)
        data = await _smartWalletQuery(signer, userAddress);
    } catch(e) {
        // do nothing
    }

    let listOfValidators = [];

    // if the user is a solo-staker on Stakehouse, add it to the list
    const stakehouseUserActivity = await _validatorIndexQuery(signer, userAddress);
    listOfValidators = listOfValidators.concat(stakehouseUserActivity);

    if(data) {
        let previousSmartWallet;
        let indexOwner;
        // loop through the data and fetch stakehouse related data
        // add the stakehouse related data to the list
        for(let i=0; i<data.length; ++i) {
            const currentSmartWallet = data[i].id;

            // optimization to avoid fetching index owner for same smart wallets
            if(previousSmartWallet != currentSmartWallet) {
                const req = await _getIndexOwner(signer, userAddress);
                indexOwner = req[i].liquidStakingNetwork.savETHPool;
                previousSmartWallet = currentSmartWallet;
            }

            const validatorData = await _validatorIndexQuery(signer, indexOwner);
            listOfValidators = listOfValidators.concat(validatorData);
        }
    }

    listOfValidators.sort((a, b) => {
        return a.id - b.id;
    });

    // contains list of all the stakehouse accounts associated with user's smart wallets
    return listOfValidators;
};

const _validatorIndexDetailsQuery = async (signer, indexOwner) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query Knot($account: String!) {
            savETHIndexes(where: { indexOwner: $account }, orderBy: indexId, orderDirection: asc) {
                numberOfKnots
                indexId
                indexOwner
                label
                dETHPerBlock
                dETHPerYear
                dETHTotal
                dETHTotalRewards
                id
                reportedYield
                savETHTotal
                isolationExchangeRate
            }
        }
	`;

    const response = await request(
		stakehouseUrls.SUBGRAPH_ENDPOINTS,
		lookupQuery,
        {
            account: _add0x(indexOwner.toLowerCase())
        }
	);

    if (!response || !response.savETHIndexes) {
		throw new Error('Invalid response getting the validators indexes');
	}

    return response.savETHIndexes;
};

const _getCumulativeValidatorIndexDetails = async (signer, userAddress) => {

    let data;

    try {
        // get all the smart wallets associated with the userAddress (node operator in LSD)
        data = await _smartWalletQuery(signer, userAddress);
    } catch(e) {
        // do nothing
    }

    let listOfValidators = [];

    if(data) {
        let previousSmartWallet;
        let indexOwner;
        // loop through the data and fetch stakehouse related data
        // add the stakehouse related data to the list
        for(let i=0; i<data.length; ++i) {
            const currentSmartWallet = data[i].id;

            // optimization to avoid fetching index owner for same smart wallets
            if(previousSmartWallet != currentSmartWallet) {
                const req = await _getIndexOwner(signer, userAddress);
                indexOwner = req[i].liquidStakingNetwork.savETHPool;
                previousSmartWallet = currentSmartWallet;
            }

            const validatorData = await _validatorIndexDetailsQuery(signer, indexOwner);
            listOfValidators = listOfValidators.concat(validatorData);
        }
    }

    // if the user is a solo-staker on Stakehouse, add it to the list
    const stakehouseUserIndexDetails = await _validatorIndexDetailsQuery(signer, userAddress);
    listOfValidators = listOfValidators.concat(stakehouseUserIndexDetails);

    listOfValidators.sort((a, b) => {
        return a.indexId - b.indexId;
    });

    // contains list of all the stakehouse accounts associated with user's smart wallets
    return listOfValidators;
};

const _batchDepositNodeOperators = async (signer, liquidStakingManagerAddress, batchDepositData, eoaRepresentative, ethAmount) => {

    const number = ethAmount.div(ONE_ETHER.mul(ethers.BigNumber.from("4")));
    if(number.toString() != batchDepositData.length) {
        throw new Error(customErrors.INSUFFICIENT_BATCH_DEPOSIT);
    }

    let blsPublicKeys = [];
    let blsSignatures = [];

    for(let i=0; i<batchDepositData.length; ++i) {
        blsPublicKeys.push(_add0x(batchDepositData[i].pubkey));
        blsSignatures.push(_add0x(batchDepositData[i].signature));
    }

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    try {
        const safeOwners = await _getSafeOwners(eoaRepresentative);

        if(safeOwners.length > 0) {
            eoaRepresentative = safeOwners[0];
        }
    }
    catch(e) {
        // do nothing
    }

    return contract.registerBLSPublicKeys(
        blsPublicKeys,
        blsSignatures,
        _add0x(eoaRepresentative),
        { value: ethAmount }
    );
};

const _batchStake = async (signer, liquidStakingManagerAddress, authenticationPackages) => {

    let blsPublicKeys = [];
    let cipherTexts = [];
    let encryptorKeys = [];
    let blsSignatures = [];
    let depositDataRoots = [];

    for(let i=0; i<authenticationPackages.length; ++i) {

        const { deposit_data, cipher_text, encryptor_aes_key, deadline, v, r, s } = authenticationPackages[i];
        const signature = { deadline, v, r: _add0x(r), s: _add0x(s) };
        const blsPublicKey = _add0x(deposit_data[0].pubkey);

        _isBLSPublicKeyBanned(signer, liquidStakingManagerAddress, blsPublicKey);

        blsPublicKeys.push(blsPublicKey);
        cipherTexts.push(_add0x(cipher_text));
        encryptorKeys.push(encryptor_aes_key);
        blsSignatures.push(signature);
        depositDataRoots.push(_add0x(deposit_data[0].deposit_data_root));
    }

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    return contract.stake(
        blsPublicKeys,
        cipherTexts,
        encryptorKeys,
        blsSignatures,
        depositDataRoots
    )
};

const _batchMintDerivatives = async (signer, liquidStakingManagerAddress, authenticatedBalanceReports) => {

    let blsPublicKeys = [];
    let balanceReports = [];
    let signatures = [];

    for(let i=0; i<authenticatedBalanceReports.length; ++i) {
        const { deadline, r, report, s, v } = authenticatedBalanceReports[i];
        const blsPublicKey = _add0x(report.blsPublicKey);
        const withdrawalCredentials = _add0x(report.withdrawalCredentials);
        const formattedReport = {
            ...report,
            blsPublicKey: blsPublicKey,
            withdrawalCredentials: withdrawalCredentials
        }
        const formattedSignatures = {
            deadline: deadline,
            v: v,
            r: _add0x(r),
            s: _add0x(s)
        };

        blsPublicKeys.push(blsPublicKey);
        balanceReports.push(formattedReport);
        signatures.push(formattedSignatures);
    }

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    return contract.mintDerivatives(
        blsPublicKeys,
        balanceReports,
        signatures
    );
};

const _isLSD = async (signer, ticker) => {

    let lsd = ticker.toLowerCase();

    if(lsd[0] === 's') {
        lsd = lsd.slice(1);
    }

    const chainID = await _extractChainID(signer);
	  const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query LSD {
            liquidStakingNetworks {
                ticker
            }
        }
    `;

    const response1 = await request(
        stakehouseUrls.V1_LSD_SUBGRAPH_ENDPOINT,
        lookupQuery
    );

    const response2 = await request(
        stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
        lookupQuery
    );

    if(response1) {
        for(let i=0; i<response1.liquidStakingNetworks.length; ++i) {
            const target = response1.liquidStakingNetworks[i].ticker.toLowerCase();
            if(target === lsd) {
                return true;
            }
        }
    }

    if(response2) {
        for(let i=0; i<response2.liquidStakingNetworks.length; ++i) {
            const target = response2.liquidStakingNetworks[i].ticker.toLowerCase();
            if(target === lsd) {
                return true;
            }
        }
    }

    return false;
};

const _executeFunctionAsSmartWallet = async (signer, nodeRunner, targetContractAddress, encodedFunctionData, ethValue) => {

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManager);

    return contract.executeAsSmartWallet(
        _add0x(nodeRunner),
        _add0x(targetContractAddress),
        encodedFunctionData, // function signature
        ethValue
    );
};

const _applyForDecryptionAsSmartWallet = async (signer, liquidStakingManager, blsPublicKey, stakehouseAddress, aesPublicKey, nodeRunner) => {

	const chainID = await _extractChainID(signer);
	const { stakehouseAddresses } = _getChainSpecificConstants(chainID);

    const inputParams = [
        _add0x(blsPublicKey),
        _add0x(stakehouseAddress),
        Buffer.from(_remove0x(aesPublicKey), 'hex')
    ];

    const safeBoxInterface = new ethers.utils.Interface(stakehouseContracts.SAFE_BOX);

    const encodedFunctionData = safeBoxInterface.encodeFunctionData(
        "applyForDecryption",
        inputParams
    );

    const contract = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManager);

    return contract.executeAsSmartWallet(
        _add0x(nodeRunner),
        _add0x(stakehouseAddresses.SAFE_BOX),
        encodedFunctionData,
        ethers.utils.parseEther("0")
    );
};

const _setNodeRunnerLabel = async (signer, label) => {

    if(!_.isString(label)) {
        throw new Error("Label must be a string");
    }

    const contract = (await getContractInstance(signer)).smartWalletNaming();

    return contract.updateName(label);
};

const _getSmartWalletLabel = async (signer, liquidStakingManagerAddress, smartWalletAddress) => {

    const contract = (await getContractInstance(signer)).smartWalletNaming();

    return contract.getSmartWalletName(
        _add0x(liquidStakingManagerAddress),
        _add0x(smartWalletAddress)
    );
};

const _ownerOfLabel = async (signer, label) => {

    if(!_.isString(label)) {
        throw new Error("Label must be a string");
    }

    const contract = (await getContractInstance(signer)).smartWalletNaming();

    return contract.nameAlreadyTaken(label);
};

const _isLabelAlreadyTaken = async (signer, label) => {

    const address = await _ownerOfLabel(signer, label);

    return address == ethers.constants.AddressZero ? false : true;
};

const _depositDETHForStaking = async (signer, savETHPoolAddress, blsPublicKey, amount) => {

    const contract = (await getContractInstance(signer)).savETHPool(savETHPoolAddress);

    return contract.depositDETHForStaking(
        _add0x(blsPublicKey),
        amount
    );
};

const _getdETHRequiredToIsolateWithdrawnKnot = async (signer, savETHPoolAddress, blsPublicKey) => {

    const contract = (await getContractInstance(signer)).savETHPool(savETHPoolAddress);

    return contract.dETHRequiredToIsolateWithdrawnKnot(
        _add0x(blsPublicKey)
    );
};

const _batchCalculateFundsRequiredForStaking = async (signer, blsPublicKeys) => {

    let promises = [];

    for(let i=0; i<blsPublicKeys.length; ++i) {
        const promise = _calculateFundsRequiredForStaking(signer, blsPublicKeys[i]);
        promises.push(promise);
    }

    let res = [];

    await Promise.allSettled(promises).then(
        async(result) => {
            for(let i=0; i<result.length; ++i) {
                res.push(result[i].value);
            }
        }
    );
    
    return res;
};

const _batchFundNodeOperatorFromGiantSavETHPool = async (signer, liquidStakingManagerAddress, blsPublicKeys, amounts) => {

    const len = blsPublicKeys.length;
    if(len == 0) {
        throw new Error(customErrors.NULL_OR_UNDEFINED_VALUE);
    }
    else if(len != amounts.length) {
        throw new Error(customErrors.ARRAY_LENGTH_MISMATCH);
    }

    let totalAmount = ZERO;

    for(let i=0; i<len; ++i) {
        if(BigNumber.from(amounts[i]).eq(ZERO)) {
            throw new Error(customErrors.AMOUNT_CANNOT_BE_ZERO);
        }
        else if(BigNumber.from(amounts[i]).gt(ONE_ETHER.mul('24'))) {
            throw new Error(customErrors.INCORRECT_FUND_AMOUNT_FOR_GIANT_SAVETH_VAULT);
        }
        
        blsPublicKeys[i] = _add0x(blsPublicKeys[i]);

        if(ethers.BigNumber.isBigNumber(amounts[i])) {
            totalAmount = totalAmount.add(amounts[i]);
        }
        else {
            totalAmount = totalAmount.add(ethers.BigNumber.from(amounts[i]));
        }
    }

    const liquidStakingManager = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    const savETHVault = await liquidStakingManager.savETHVault();

    const giantSavETHPool = (await getContractInstance(signer)).giantSavETHPool();

    return giantSavETHPool.batchDepositETHForStaking(
        [savETHVault], // address of savETH vault which gets ETH from giant pool
        [totalAmount], // amount of ETH being transferred to savETH vault
        [blsPublicKeys], // list of BLS public keys for which ETH is being deposited
        [amounts] // amount of ETH to be deposited for each BLS public key
    );
};

const _batchFundNodeOperatorFromGiantFeesAndMevPool = async (signer, liquidStakingManagerAddress, blsPublicKeys, amounts) => {

    const len = blsPublicKeys.length;
    if(len == 0) {
        throw new Error(customErrors.NULL_OR_UNDEFINED_VALUE);
    }
    else if(len != amounts.length) {
        throw new Error(customErrors.ARRAY_LENGTH_MISMATCH);
    }

    let totalAmount = ZERO;

    for(let i=0; i<len; ++i) {
        if(BigNumber.from(amounts[i]).eq(ZERO)) {
            throw new Error(customErrors.AMOUNT_CANNOT_BE_ZERO);
        }
        else if(BigNumber.from(amounts[i]).gt(ONE_ETHER.mul('4'))) {
            throw new Error(customErrors.INCORRECT_FUND_AMOUNT_FOR_GIANT_FEES_AND_MEV);
        }
        
        blsPublicKeys[i] = _add0x(blsPublicKeys[i]);

        if(ethers.BigNumber.isBigNumber(amounts[i])) {
            totalAmount = totalAmount.add(amounts[i]);
        }
        else {
            totalAmount = totalAmount.add(ethers.BigNumber.from(amounts[i]));
        }
    }

    const liquidStakingManager = (await getContractInstance(signer)).liquidStakingManager(liquidStakingManagerAddress);

    const stakingFundsVault = await liquidStakingManager.stakingFundsVault();

    const giantStakingFundsPool = (await getContractInstance(signer)).giantFeesAndMEV();

    return giantStakingFundsPool.batchDepositETHForStaking(
        [stakingFundsVault], // address of staking funds fault which gets ETH from giant pool
        [totalAmount], // amount of ETH to be sent to the staking funds vault
        [blsPublicKeys], // list of BLS public keys for which ETH is being deposited
        [amounts] // amount of ETH to be deposited for each BLS public key
    );
};

const _getBatchDepositDataFromKeystores = async (signer, keystores, keystorePassword) => {

    let res = [];
    let promises = [];

    for(let i=0; i<keystores.length; ++i) {
        const promise = _getDepositDataFromKeystore(signer, keystores[i], keystorePassword);
        promises.push(promise);
    }

    await Promise.allSettled(promises).then(
        async(result) => {
            for(let i=0; i<result.length; ++i) {
                res = res.concat(result[i].value);
            }
        }
    );
    
    return res;
};

const _getValidatorFromSubgraph = async (signer, blsPublicKey) => {

    const chainID = await _extractChainID(signer);
	const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    const lookupQuery = gql`
        query Knot($blsPublicKey: String!) {
            lsdvalidators(where:{
              id: $blsPublicKey
            }) {
              smartWallet {
                id
                nodeRunner {
                  id
                }
                liquidStakingNetwork {
                  id
                  ticker
                  feeRecipientAndSyndicate
                }
              }
              status
            }
        }
	`;

    const response = await request(
		stakehouseUrls.LSD_SUBGRAPH_ENDPOINT,
		lookupQuery,
        {
            blsPublicKey: _add0x(blsPublicKey.toLowerCase())
        }
	);

    if (!response || !response.lsdvalidators) {
		throw new Error('Invalid response getting the validators indexes');
	}

    return response.lsdvalidators[0];
};

const _getValidatorDetails = async (signer, blsPublicKey) => {
    
    try {
        const res = await _getValidatorFromSubgraph(signer, blsPublicKey);

        const feeRecipient = res.smartWallet.liquidStakingNetwork.feeRecipientAndSyndicate;
        const smartWallet = res.smartWallet.id;
        const nodeRunner = res.smartWallet.nodeRunner.id;
        const lsd = res.smartWallet.liquidStakingNetwork.ticker;
        const status = res.status;
        const liquidStakingManager = res.smartWallet.liquidStakingNetwork.id;

        return {
            liquidStakingManager, feeRecipient, nodeRunner, smartWallet, status, lsd
        };
    }
    catch(e) {
        return;
    }
};

module.exports = {
    _depositETHForProtectedStaking,
    _depositETHForFeesAndMEV,
    _getListOfLSDNetworks,
    _getLSDNForBLSPublicKey,
    _depositETHByNodeRunner,
    _availableToStake,
    _isBLSPublicKeyBanned,
    _calculateFundsRequiredForStaking,
    _fundNodeOperatorFromGiantSavETHPool,
    _fundNodeOperatorFromGiantFeesAndMevPool,
    _stake,
    _getAllBLSPublicKeysForANodeRunnerInAnLSDNetwork,
    _previewNodeOperatorRewards,
    _getGiantPoolDETHBalance,
    _getUserGiantProtectedStakingLPBalance,
    _getUserGiantFeesAndMevLPBalance,
    _getMinimum,
    _claimFeesAndMevRewards,
    _getProtectedBatchesForDepositor,
    _previewClaimableProtectedStakingLP,
    _claimProtectedStakingRewards,
    _getValidatorsToBeRotated,
    _rotateFundsBackToGiantProtectedStakingPool,
    _rotateFundsBackToGiantFeesAndMevPool,
    _isETHAvailableInProtectedStakingOfSomeLSDN,
    _isETHAvailableInFeesAndMevOfSomeLSDN,
    _getLpTokensDataForClaimRewards,
    _previewFeesAndMevRewards,
    _getLastInteractedTimestamp,
    _isEligibleToInteractWithGiantLPToken,
    _getCumulativeListOfValidators,
    _getCumulativeActivity,
    _getCumulativeHouseCreated,
    _getCumulativeHouseJoined,
    _getCumulativeValidatorsCreated,
    _getCumulativeValidatorIndexes,
    _getCumulativeValidatorIndexDetails,
    _batchDepositNodeOperators,
    _batchStake,
    _batchMintDerivatives,
    _isLSD,
    _executeFunctionAsSmartWallet,
    _applyForDecryptionAsSmartWallet,
    _setNodeRunnerLabel,
    _getSmartWalletLabel,
    _ownerOfLabel,
    _isLabelAlreadyTaken,
    _depositDETHForStaking,
    _getdETHRequiredToIsolateWithdrawnKnot,
    _batchCalculateFundsRequiredForStaking,
    _batchFundNodeOperatorFromGiantSavETHPool,
    _batchFundNodeOperatorFromGiantFeesAndMevPool,
    _getBatchDepositDataFromKeystores,
    _getSafeOwners,
    _getValidatorDetails
};
